import { Extension } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { defaultMarkdownSerializer, type MarkdownSerializerState } from "@tiptap/pm/markdown";
import { BLANK_LINE_MARKER, normalizeMemoDoc } from "@/lib/memoDocFidelity";

/**
 * The screenshot node of a Protokoll entry (RichTextEditor `variant="memo"` uses it in
 * place of the plain Image).
 *
 * tiptap-markdown 0.8.10 writes an image with prosemirror-markdown's serializer, which
 * never closes the block, so whatever follows lands on the image's line
 * ("![a](/x.png)Nachher"). For the blank-line marker that is fatal: a blank line typed
 * after a screenshot was stored as "![a](/x.png)" plus U+00A0, so the saved view, print and
 * "Nur Neues" showed no blank line and the editor reopened it as a line holding a
 * no-break space. Closing the block puts the next paragraph, blank line or list on a
 * line of its own. Old bodies are not rewritten by this: an unedited point reports what
 * it loaded (RichTextEditor `reportedMarkdown`).
 */
export const MemoImage = Image.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(
          state: MarkdownSerializerState,
          node: ProseMirrorNode,
          parent: ProseMirrorNode,
          index: number,
        ) {
          defaultMarkdownSerializer.nodes.image?.(state, node, parent, index);
          state.closeBlock(node);
        },
      },
    };
  },
});

/**
 * Protokoll entries only (RichTextEditor `variant="memo"`): what the writer sees
 * while editing is what gets saved.
 *
 * Saving: `editor.storage.markdown.getMarkdown` serializes `normalizeMemoDoc(doc)`
 * instead of the raw document, so empty bullets are never stored and a blank line
 * between two blocks is stored as a paragraph holding `BLANK_LINE_MARKER`. Every
 * caller in RichTextEditor goes through `getMarkdown` (onUpdate, blur, Ctrl+Enter,
 * the deferred exit after an upload, the value-sync effect), so all of them get
 * the normalized Markdown. Except while the document is unedited: then
 * RichTextEditor reports the Markdown it loaded, byte for byte (`reportedMarkdown`),
 * so opening and closing an old point never rewrites it.
 *
 * Loading: tiptap-markdown runs `markdown.parse.updateDOM` of EVERY extension on
 * the HTML it renders from the stored Markdown (MarkdownMention relies on the same
 * hook). Here it turns each marker paragraph back into a truly empty paragraph,
 * so the writer gets an empty line to type into, not a line holding an invisible
 * no-break space.
 */
export const MemoFidelity = Extension.create({
  name: "memoFidelity",

  // Load-bearing: below tiptap-markdown's 50. Higher priority runs first, and the Markdown
  // extension CREATES `editor.storage.markdown` in its own onBeforeCreate. Ours
  // must run after it, or there is nothing to swap.
  priority: 40,

  // onBeforeCreate, not onCreate: TipTap emits `create` on a setTimeout(0), and
  // RichTextEditor can serialize before that (the "+ Aufgabe" prime types an "@"
  // from a mount effect). In that window the raw serializer would DROP every
  // blank line the load just turned into an empty paragraph.
  onBeforeCreate() {
    const markdown = this.editor.storage.markdown;
    if (!markdown?.serializer) {
      throw new Error("MemoFidelity needs the tiptap-markdown extension, registered before it");
    }
    markdown.getMarkdown = () =>
      markdown.serializer.serialize(normalizeMemoDoc(this.editor.state.doc)) as string;
  },

  addStorage() {
    return {
      markdown: {
        parse: {
          updateDOM(element: HTMLElement) {
            // Decode exactly what normalizeMemoDoc encodes, nothing more. Its
            // markers only ever sit BETWEEN top-level blocks, so:
            // - only top-level paragraphs (a no-break-space paragraph in a quote
            //   or a list is the writer's text, and emptying it there would lose
            //   it on the next save, because nothing re-encodes it);
            // - never the first or last block (a stored body that starts with
            //   such a paragraph keeps it, and saves byte-identically);
            // - only a paragraph whose ONLY child is the marker text (judged by
            //   its text alone, a screenshot sharing the paragraph with a
            //   no-break space would be emptied out with it).
            const blocks = Array.from(element.children);
            for (let i = 1; i < blocks.length - 1; i++) {
              const block = blocks[i];
              if (!block || block.tagName !== "P" || block.childNodes.length !== 1) continue;
              const only = block.firstChild;
              if (only?.nodeType === Node.TEXT_NODE && only.textContent === BLANK_LINE_MARKER) {
                block.replaceChildren();
              }
            }
          },
        },
      },
    };
  },
});
